import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'umulmevents_cookie_consent';

const updateGoogleConsent = (granted) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const value = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const choice = localStorage.getItem(STORAGE_KEY);
    if (!choice) {
      setVisible(true);
    } else if (choice === 'accepted') {
      // Re-apply consent on subsequent page loads
      updateGoogleConsent(true);
    }

    // Allow user to re-open banner via global function (e.g. from footer link)
    window.openCookieSettings = () => setVisible(true);
    return () => {
      delete window.openCookieSettings;
    };
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    updateGoogleConsent(true);
    setVisible(false);
  };

  const reject = () => {
    localStorage.setItem(STORAGE_KEY, 'rejected');
    updateGoogleConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9998] p-4 md:p-6 animate-in slide-in-from-bottom duration-300"
      data-testid="cookie-banner"
    >
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="hidden md:flex bg-red-50 w-12 h-12 rounded-full items-center justify-center flex-shrink-0">
              <Cookie className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg md:text-xl font-bold text-gray-900">
                  Wir respektieren deine Privatsphäre
                </h3>
                <button
                  onClick={reject}
                  className="md:hidden text-gray-400 hover:text-gray-600"
                  aria-label="Schließen"
                  data-testid="cookie-close-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Wir verwenden Cookies und ähnliche Technologien (z. B. Google Ads), um die Nutzung
                unserer Website zu analysieren, Werbung zu personalisieren und unser Angebot zu
                verbessern. Du kannst frei entscheiden, ob du das erlaubst. Mehr Informationen
                findest du in unserer{' '}
                <Link to="/datenschutz" className="text-red-600 hover:underline font-medium">
                  Datenschutzerklärung
                </Link>
                .
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={accept}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors order-1 sm:order-2 flex-1 sm:flex-initial"
                  data-testid="cookie-accept-btn"
                >
                  Alle akzeptieren
                </button>
                <button
                  onClick={reject}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold px-6 py-3 rounded-lg transition-colors order-2 sm:order-1 flex-1 sm:flex-initial"
                  data-testid="cookie-reject-btn"
                >
                  Nur notwendige
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
